import { useEffect, useRef, useState } from "react";
import { Copy, Eye, EyeOff, GripVertical, ImagePlus, Pencil, Plus, RotateCcw, Rocket, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import HomeSections, { mediaUrl } from "../../components/storefront/HomeSections";

const inputCls = "w-full h-10 px-3.5 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500";
const SECTION_TYPES = [
  ["HERO", "Hero Alanı"], ["BANNER", "Banner"], ["CATEGORY_GRID", "Kategori Alanı"],
  ["FEATURED_PRODUCTS", "Ürün Grubu"], ["IMAGE_TEXT", "Görsel + Metin"], ["FEATURE_LIST", "Özellik/Güven Listesi"],
  ["MARQUEE", "Vitrin Şeridi"], ["BRANDS", "Markalar"], ["NEWSLETTER", "Bülten"], ["CUSTOM_HTML", "Özel HTML (Süper Admin)"],
];
const EMPTY_SECTION = { type: "HERO", title: "", subtitle: "", description: "", image: "", mobile_image: "", alt_text: "", button_text: "", button_url: "", is_active: true, sort_order: 99, settings: {} };

export default function HomepagePage() {
  const [tab, setTab] = useState("bolumler");
  const [sections, setSections] = useState([]);
  const [published, setPublished] = useState(null);
  const [versions, setVersions] = useState([]);
  const [homeData, setHomeData] = useState(null);
  const [form, setForm] = useState(null);
  const [media, setMedia] = useState([]);
  const [mediaPicker, setMediaPicker] = useState(null); // "image" | "mobile_image" | null
  const [previewWidth, setPreviewWidth] = useState("100%");
  const [seo, setSeo] = useState({ seo_title: "", seo_description: "", og_title: "", og_description: "", og_image: "" });
  const [popup, setPopup] = useState(null);
  const dragId = useRef(null);

  const load = () => {
    api.get("/admin/homepage/sections").then(({ data }) => { setSections(data.sections); setPublished(data.published); }).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/homepage/versions").then(({ data }) => setVersions(data)).catch(() => {});
    api.get("/admin/homepage/popup").then(({ data }) => setPopup(data)).catch(() => {});
  };

  const savePopup = async () => {
    try {
      await api.put("/admin/homepage/popup", { ...popup, image_url: popup.image_url || null });
      toast.success("Popup kaydedildi (anında yayında)");
    } catch (err) { toast.error(errMsg(err)); }
  };
  useEffect(() => {
    load();
    api.get("/home").then(({ data }) => setHomeData(data)).catch(() => {});
    api.get("/admin/homepage/media").then(({ data }) => setMedia(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSeo((s) => ({ ...s, seo_title: data.seo_title || "", seo_description: data.seo_description || "" }))).catch(() => {});
  }, []);

  const saveSection = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, image: form.image || null, mobile_image: form.mobile_image || null };
      if (form.section_id) await api.put(`/admin/homepage/sections/${form.section_id}`, payload);
      else await api.post("/admin/homepage/sections", payload);
      toast.success("Bölüm taslak olarak kaydedildi (yayınlamayı unutmayın)");
      setForm(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const toggleActive = async (s) => {
    await api.put(`/admin/homepage/sections/${s.section_id}`, { ...s, is_active: !s.is_active });
    load();
  };

  const doPublish = async () => {
    if (!window.confirm("Taslak değişiklikler canlı siteye yayınlansın mı?")) return;
    try {
      const { data } = await api.post("/admin/homepage/publish");
      toast.success(`Versiyon ${data.version_no} yayında (${data.section_count} bölüm)`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doRollback = async (v) => {
    if (!window.confirm(`Versiyon ${v} geri yüklensin ve yayınlansın mı?`)) return;
    try {
      const { data } = await api.post(`/admin/homepage/rollback/${v}`);
      toast.success(`Geri yüklendi, versiyon ${data.version_no} olarak yayında`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const onDrop = async (targetId) => {
    const from = dragId.current;
    if (!from || from === targetId) return;
    const ids = sections.map((s) => s.section_id);
    const fromIdx = ids.indexOf(from);
    const toIdx = ids.indexOf(targetId);
    ids.splice(toIdx, 0, ids.splice(fromIdx, 1)[0]);
    setSections(ids.map((id) => sections.find((s) => s.section_id === id)));
    try {
      await api.put("/admin/homepage/order", { order: ids });
      toast.success("Sıralama taslakta güncellendi");
    } catch (err) { toast.error(errMsg(err)); load(); }
  };

  const uploadMedia = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", file.name);
    try {
      await api.post("/admin/homepage/media", fd);
      const { data } = await api.get("/admin/homepage/media");
      setMedia(data);
      toast.success("Görsel yüklendi");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const pickMedia = (m) => {
    if (mediaPicker === "popup_image") setPopup((p) => ({ ...p, image_url: m.optimized_url || m.url }));
    else if (mediaPicker && form) setForm({ ...form, [mediaPicker]: m.optimized_url || m.url });
    setMediaPicker(null);
  };

  const typeLabel = (t) => SECTION_TYPES.find(([v]) => v === t)?.[1] || t;

  return (
    <div className="space-y-5" data-testid="admin-homepage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-white">Ana Sayfa Yönetimi</h1>
          <p className="text-xs text-neutral-500 mt-1">
            {published ? `Yayında: Versiyon ${published.version_no} · ${fmtDate(published.published_at)} · ${published.published_by}` : "Henüz yayın yok"}
          </p>
        </div>
        <button onClick={doPublish} className="h-10 px-5 rounded-xl bg-emerald-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-emerald-500" data-testid="homepage-publish">
          <Rocket size={15} /> Değişiklikleri Yayınla
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[["bolumler", "Bölümler"], ["onizleme", "Önizleme"], ["medya", "Medya Kütüphanesi"], ["popup", "Popup"], ["seo", "SEO"], ["versiyonlar", "Versiyonlar"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`h-9 px-4 rounded-xl text-xs font-semibold transition-colors ${tab === id ? "bg-blue-600 text-white" : "bg-[#181B20] text-neutral-400 border border-[#272B33] hover:text-white"}`}
            data-testid={`homepage-tab-${id}`}>{label}</button>
        ))}
      </div>

      {tab === "bolumler" && (
        <>
          <div className="flex justify-between items-center">
            <p className="text-xs text-neutral-500">Sürükleyerek sıralayın. Değişiklikler yayınlanana kadar canlı site değişmez.</p>
            <button onClick={() => setForm({ ...EMPTY_SECTION })} className="h-10 px-4 rounded-xl bg-blue-600 text-white text-sm font-semibold flex items-center gap-2 hover:bg-blue-500" data-testid="section-new">
              <Plus size={15} /> Yeni Bölüm
            </button>
          </div>

          {form && (
            <form onSubmit={saveSection} className="bg-[#181B20] border border-[#272B33] rounded-2xl p-5 space-y-3" data-testid="section-form">
              <div className="grid sm:grid-cols-3 gap-3">
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="section-type">
                  {SECTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input placeholder="Başlık" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="section-title" />
                <input placeholder="Alt başlık" value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className={inputCls} data-testid="section-subtitle" />
              </div>
              <textarea placeholder="Açıklama" rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} py-2.5 resize-none`} data-testid="section-description" />
              <div className="grid sm:grid-cols-2 gap-3">
                {["image", "mobile_image"].map((field) => (
                  <div key={field} className="space-y-1.5">
                    <label className="text-[10px] text-neutral-500 uppercase">{field === "image" ? "Desktop Görseli" : "Mobil Görseli (opsiyonel)"}</label>
                    <div className="flex gap-2">
                      <input placeholder="https://... veya kütüphaneden seç" value={form[field] || ""} onChange={(e) => setForm({ ...form, [field]: e.target.value })} className={inputCls} data-testid={`section-${field}`} />
                      <button type="button" onClick={() => setMediaPicker(field)} className="h-10 px-3 rounded-xl border border-[#272B33] text-neutral-400 hover:text-white shrink-0" data-testid={`section-${field}-pick`}><ImagePlus size={15} /></button>
                    </div>
                    {form[field] && <img src={mediaUrl(form[field])} alt="" className="h-16 rounded-lg object-cover" />}
                  </div>
                ))}
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <input placeholder="Buton yazısı" value={form.button_text || ""} onChange={(e) => setForm({ ...form, button_text: e.target.value })} className={inputCls} data-testid="section-button-text" />
                <input placeholder="Buton linki (/ara gibi)" value={form.button_url || ""} onChange={(e) => setForm({ ...form, button_url: e.target.value })} className={inputCls} data-testid="section-button-url" />
                <input placeholder="Alt text (SEO)" value={form.alt_text || ""} onChange={(e) => setForm({ ...form, alt_text: e.target.value })} className={inputCls} data-testid="section-alt" />
              </div>
              {form.type === "HERO" && (
                <div className="grid sm:grid-cols-3 gap-3">
                  <input placeholder="Buton 2 yazısı" value={form.settings?.button2_text || ""} onChange={(e) => setForm({ ...form, settings: { ...form.settings, button2_text: e.target.value } })} className={inputCls} data-testid="section-button2-text" />
                  <input placeholder="Buton 2 linki" value={form.settings?.button2_url || ""} onChange={(e) => setForm({ ...form, settings: { ...form.settings, button2_url: e.target.value } })} className={inputCls} />
                  <select value={form.settings?.text_align || "LEFT"} onChange={(e) => setForm({ ...form, settings: { ...form.settings, text_align: e.target.value } })} className={inputCls} data-testid="section-align">
                    <option value="LEFT">Sola hizalı</option><option value="CENTER">Ortalı</option><option value="RIGHT">Sağa hizalı</option>
                  </select>
                </div>
              )}
              {form.type === "FEATURED_PRODUCTS" && (
                <div className="grid sm:grid-cols-3 gap-3">
                  <select value={form.settings?.auto_rule || "featured"} onChange={(e) => setForm({ ...form, settings: { ...form.settings, auto_rule: e.target.value } })} className={inputCls} data-testid="section-auto-rule">
                    <option value="featured">Öne çıkanlar</option><option value="new">Yeni ürünler</option>
                    <option value="discounted">İndirimli ürünler</option><option value="bestsellers">Çok satanlar</option>
                  </select>
                  <input type="number" placeholder="Ürün sayısı" value={form.settings?.limit || 8} onChange={(e) => setForm({ ...form, settings: { ...form.settings, limit: parseInt(e.target.value) || 8 } })} className={inputCls} />
                  <select value={form.settings?.theme || "light"} onChange={(e) => setForm({ ...form, settings: { ...form.settings, theme: e.target.value } })} className={inputCls} data-testid="section-theme">
                    <option value="light">Açık tema</option><option value="dark">Koyu tema</option>
                  </select>
                </div>
              )}
              {form.type === "MARQUEE" && (
                <input placeholder="Şerit öğeleri (virgülle ayırın)" value={(form.settings?.items || []).join(", ")} onChange={(e) => setForm({ ...form, settings: { ...form.settings, items: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } })} className={inputCls} data-testid="section-marquee-items" />
              )}
              {form.type === "IMAGE_TEXT" && (
                <select value={form.settings?.layout || "IMAGE_LEFT"} onChange={(e) => setForm({ ...form, settings: { ...form.settings, layout: e.target.value } })} className={inputCls} data-testid="section-layout">
                  <option value="IMAGE_LEFT">Görsel solda</option><option value="IMAGE_RIGHT">Görsel sağda</option>
                </select>
              )}
              {form.type === "CUSTOM_HTML" && (
                <textarea placeholder="HTML içerik (script/iframe engellenir)" rows={4} value={form.settings?.html || ""} onChange={(e) => setForm({ ...form, settings: { ...form.settings, html: e.target.value } })} className={`${inputCls} py-2.5 font-mono-ed text-xs resize-none`} data-testid="section-html" />
              )}
              <div className="flex gap-3 items-center flex-wrap">
                <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-blue-500 w-4 h-4" data-testid="section-active" /> Aktif</label>
                <button type="submit" className="h-10 px-5 rounded-xl bg-blue-600 text-white text-sm font-semibold" data-testid="section-save">Kaydet (Taslak)</button>
                <button type="button" onClick={() => setForm(null)} className="h-10 px-4 rounded-xl border border-[#272B33] text-sm text-neutral-400">Vazgeç</button>
              </div>
            </form>
          )}

          <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden" data-testid="section-list">
            {sections.map((s) => (
              <div key={s.section_id}
                draggable
                onDragStart={() => { dragId.current = s.section_id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(s.section_id)}
                className="flex items-center gap-3 px-4 py-3.5 border-b border-[#272B33]/50 last:border-0 hover:bg-[#1E222A]/50"
                data-testid={`section-row-${s.section_id}`}>
                <GripVertical size={15} className="text-neutral-600 cursor-grab shrink-0" />
                <span className="text-[10px] font-mono-ed text-neutral-600 w-6">{s.sort_order}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{s.title || typeLabel(s.type)}</p>
                  <p className="text-[10px] text-neutral-500 font-mono-ed uppercase">{typeLabel(s.type)}</p>
                </div>
                {!s.is_active && <span className="text-[10px] font-bold text-rose-400">PASİF</span>}
                <div className="flex gap-1">
                  <button onClick={() => toggleActive(s)} className="p-2 text-neutral-400 hover:text-white" title={s.is_active ? "Pasifleştir" : "Aktifleştir"} data-testid="section-toggle">
                    {s.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                  <button onClick={() => setForm({ ...EMPTY_SECTION, ...s, image: s.image || "", mobile_image: s.mobile_image || "" })} className="p-2 text-neutral-400 hover:text-blue-400" data-testid="section-edit"><Pencil size={15} /></button>
                  <button onClick={async () => { await api.post(`/admin/homepage/sections/${s.section_id}/duplicate`); load(); toast.success("Bölüm kopyalandı (pasif olarak)"); }} className="p-2 text-neutral-400 hover:text-white" data-testid="section-duplicate"><Copy size={15} /></button>
                  <button onClick={async () => { if (!window.confirm(`"${s.title || s.type}" bölümü silinsin mi? (Geri alınabilir)`)) return; await api.delete(`/admin/homepage/sections/${s.section_id}`); load(); toast.success("Bölüm silindi"); }} className="p-2 text-neutral-400 hover:text-rose-400" data-testid="section-delete"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === "onizleme" && (
        <div className="space-y-3" data-testid="homepage-preview">
          <div className="flex gap-2">
            {[["100%", "Desktop"], ["768px", "Tablet"], ["390px", "Mobil"]].map(([w, l]) => (
              <button key={w} onClick={() => setPreviewWidth(w)}
                className={`h-9 px-4 rounded-xl text-xs font-semibold ${previewWidth === w ? "bg-blue-600 text-white" : "bg-[#181B20] text-neutral-400 border border-[#272B33]"}`}
                data-testid={`preview-${l.toLowerCase()}`}>{l}</button>
            ))}
            <span className="text-[11px] text-amber-400 self-center ml-2">Taslak önizleme — canlı siteyi etkilemez</span>
          </div>
          <div className="bg-white rounded-2xl overflow-hidden mx-auto transition-all" style={{ width: previewWidth, maxWidth: "100%" }}>
            <HomeSections sections={sections} homeData={homeData} />
          </div>
        </div>
      )}

      {tab === "medya" && (
        <div className="space-y-4" data-testid="media-library">
          <label className="flex items-center justify-center gap-2 h-28 border-2 border-dashed border-[#272B33] rounded-2xl text-sm text-neutral-400 hover:text-white hover:border-blue-500 cursor-pointer transition-colors">
            <ImagePlus size={18} /> Görsel Yükle (JPG, PNG, WEBP, SVG — maks 10MB)
            <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="hidden"
              onChange={(e) => e.target.files[0] && uploadMedia(e.target.files[0])} data-testid="media-upload" />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {media.map((m) => (
              <div key={m.media_id} className="bg-[#181B20] border border-[#272B33] rounded-xl overflow-hidden" data-testid={`media-${m.media_id}`}>
                <img src={mediaUrl(m.optimized_url || m.url)} alt={m.alt_text} loading="lazy" className="w-full h-24 object-cover" />
                <div className="p-2">
                  <p className="text-[10px] text-neutral-400 truncate">{m.filename}</p>
                  <p className="text-[9px] text-neutral-600">{(m.size / 1024).toFixed(0)} KB · {fmtDate(m.created_at)}</p>
                </div>
              </div>
            ))}
            {media.length === 0 && <p className="text-sm text-neutral-500 col-span-full py-8 text-center">Henüz görsel yok</p>}
          </div>
        </div>
      )}

      {tab === "popup" && popup && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 max-w-2xl space-y-4" data-testid="popup-editor">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-semibold">Ana Sayfa Popup</p>
              <p className="text-[11px] text-neutral-500 mt-0.5">Değişiklikler kaydedince anında yayına girer. Kullanıcı kapattıktan sonra 3 gün gösterilmez.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={popup.active} onChange={(e) => setPopup({ ...popup, active: e.target.checked })} className="accent-blue-500 w-4 h-4" data-testid="popup-active" /> Aktif
            </label>
          </div>
          <div>
            <label className="text-[11px] text-neutral-500 block mb-1">Başlık</label>
            <input value={popup.title} onChange={(e) => setPopup({ ...popup, title: e.target.value })} className={inputCls} data-testid="popup-title-input" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500 block mb-1">Fiyat / Vurgu Metni</label>
            <input value={popup.price_text} onChange={(e) => setPopup({ ...popup, price_text: e.target.value })} className={inputCls} data-testid="popup-price-input" />
          </div>
          <div>
            <label className="text-[11px] text-neutral-500 block mb-1">Açıklama</label>
            <textarea value={popup.description} onChange={(e) => setPopup({ ...popup, description: e.target.value })} rows={2} className="w-full px-3.5 py-2.5 bg-[#0F1115] border border-[#272B33] rounded-xl text-sm text-white focus:outline-none focus:border-blue-500 resize-none" data-testid="popup-desc-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-neutral-500 block mb-1">Buton Metni</label>
              <input value={popup.button_text} onChange={(e) => setPopup({ ...popup, button_text: e.target.value })} className={inputCls} data-testid="popup-button-input" />
            </div>
            <div>
              <label className="text-[11px] text-neutral-500 block mb-1">Hedef URL</label>
              <input value={popup.target_url} onChange={(e) => setPopup({ ...popup, target_url: e.target.value })} className={inputCls} data-testid="popup-url-input" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-neutral-500 block mb-1">Görsel (opsiyonel)</label>
            <div className="flex gap-2">
              <input value={popup.image_url || ""} onChange={(e) => setPopup({ ...popup, image_url: e.target.value })} placeholder="Görsel URL veya kütüphaneden seç" className={inputCls} data-testid="popup-image-input" />
              <button type="button" onClick={() => setMediaPicker("popup_image")} className="h-10 px-3 rounded-xl border border-[#272B33] text-neutral-300 hover:border-blue-500 shrink-0" data-testid="popup-media-pick"><ImagePlus size={16} /></button>
            </div>
          </div>
          <button onClick={savePopup} className="h-10 px-6 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500" data-testid="popup-save">Kaydet</button>
        </div>
      )}

      {tab === "seo" && (
        <form onSubmit={async (e) => { e.preventDefault(); try { await api.put("/admin/homepage/seo", seo); toast.success("SEO ayarları kaydedildi"); } catch (err) { toast.error(errMsg(err)); } }}
          className="bg-[#181B20] border border-[#272B33] rounded-2xl p-6 grid gap-4 max-w-2xl" data-testid="homepage-seo-form">
          <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Meta Title</label>
            <input value={seo.seo_title} onChange={(e) => setSeo({ ...seo, seo_title: e.target.value })} className={inputCls} data-testid="seo-meta-title" /></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">Meta Description</label>
            <textarea rows={2} value={seo.seo_description} onChange={(e) => setSeo({ ...seo, seo_description: e.target.value })} className={`${inputCls} py-2.5 resize-none`} data-testid="seo-meta-description" /></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">OG Title</label>
            <input value={seo.og_title} onChange={(e) => setSeo({ ...seo, og_title: e.target.value })} className={inputCls} /></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">OG Description</label>
            <input value={seo.og_description} onChange={(e) => setSeo({ ...seo, og_description: e.target.value })} className={inputCls} /></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1.5">OG Image URL</label>
            <input value={seo.og_image} onChange={(e) => setSeo({ ...seo, og_image: e.target.value })} className={inputCls} /></div>
          <button type="submit" className="h-10 px-6 rounded-xl bg-blue-600 text-white text-sm font-semibold w-fit" data-testid="seo-save">Kaydet</button>
        </form>
      )}

      {tab === "versiyonlar" && (
        <div className="bg-[#181B20] border border-[#272B33] rounded-2xl overflow-hidden" data-testid="version-list">
          {versions.map((v) => (
            <div key={v.version_id} className="flex items-center justify-between px-5 py-3.5 border-b border-[#272B33]/50 last:border-0">
              <div>
                <p className="text-sm text-white font-semibold">Versiyon {v.version_no}</p>
                <p className="text-[11px] text-neutral-500">{fmtDate(v.created_at)} · {v.published_by}{v.note ? ` · ${v.note}` : ""}</p>
              </div>
              <button onClick={() => doRollback(v.version_no)} className="h-9 px-4 rounded-xl bg-amber-600/15 text-amber-400 text-xs font-semibold hover:bg-amber-600/25 flex items-center gap-2" data-testid={`rollback-${v.version_no}`}>
                <RotateCcw size={13} /> Bu versiyona dön
              </button>
            </div>
          ))}
          {versions.length === 0 && <p className="text-center text-neutral-500 text-sm py-10">Versiyon yok</p>}
        </div>
      )}

      {mediaPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="media-picker">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMediaPicker(null)} />
          <div className="relative bg-[#13161B] border border-[#272B33] rounded-2xl p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display font-bold text-white">Medya Kütüphanesinden Seç</h3>
              <div className="flex items-center gap-2">
                <label className="h-9 px-3 rounded-xl bg-blue-600/15 text-blue-400 text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-600/25 cursor-pointer" data-testid="picker-upload">
                  <ImagePlus size={13} /> Resim Yükle
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="hidden"
                    onChange={async (e) => { if (e.target.files[0]) { await uploadMedia(e.target.files[0]); } e.target.value = ""; }} />
                </label>
                <button onClick={() => setMediaPicker(null)} className="text-neutral-400 hover:text-white"><X size={18} /></button>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {media.map((m) => (
                <button key={m.media_id} type="button" onClick={() => pickMedia(m)} className="rounded-xl overflow-hidden border border-[#272B33] hover:border-blue-500 transition-colors" data-testid={`pick-${m.media_id}`}>
                  <img src={mediaUrl(m.optimized_url || m.url)} alt={m.alt_text} className="w-full h-24 object-cover" />
                </button>
              ))}
              {media.length === 0 && <p className="text-sm text-neutral-500 col-span-full py-6 text-center">Önce Medya sekmesinden görsel yükleyin</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
